import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of, switchMap, take } from "rxjs";
import { environment } from "src/environments/environment";
import { Employee, EmployeeCreateDTO, EmployeePagination } from "../../models/employee.interface";
import { FeatureManagementService } from "../feature-management/feature-management.service";
import { FeatureFlagCodes } from "@shared/constants/feature-flag-codes";

@Injectable({
	providedIn: "root"
})
export class EmployeeService {
	private employeeListState: Employee[] = [];

	private sortField = "data.lastName";
	private sortDir = "ASC";

	constructor(private http: HttpClient, private featureManagementService: FeatureManagementService) {}

	// Version resolution
	private getVersion$(): Observable<"v1" | "v2"> {
		return this.featureManagementService.hasFeature(FeatureFlagCodes.Q1_2026_WPAY_CPNOW_INTEGRATION).pipe(
			take(1),
			map(on => (on ? ("v2" as const) : ("v1" as const))),
			catchError(() => of<"v1">("v1"))
		);
	}

	private withVersion<T>(fn: (v: "v1" | "v2") => Observable<T>): Observable<T> {
		return this.getVersion$().pipe(switchMap(fn));
	}

	get employeeListByPaygroupId(): Employee[] {
		return this.employeeListState;
	}

	set employeeListByPaygroupId(employees: Employee[]) {
		this.employeeListState = employees;
	}

	getEmployeesByPaygroupId(id: string): Observable<EmployeePagination> {
		return this.http.get<EmployeePagination>(
			`${environment.apiUri}/v1/employees?payGroupIds=${id}&pageSize=-1&sortField=${this.sortField}&sortDir=${this.sortDir}`
		);
	}

	getEmployeesById(id: string): Observable<Employee> {
		return this.http.get<Employee>(`${environment.apiUri}/v1/employees/${id}`);
	}

	getEmployeesByIds(ids: string): Observable<EmployeePagination> {
		return this.http.get<EmployeePagination>(`${environment.apiUri}/v1/employees?ids=${ids}`);
	}

	// Writes: versioned by feature flag
	createNewEmployee(employee: EmployeeCreateDTO): Observable<Employee> {
		return this.withVersion(version =>
			this.http.post<Employee>(`${environment.apiUri}/${version}/employees`, employee)
		);
	}

	updateEmployee(employee: EmployeeCreateDTO): Observable<Employee> {
		return this.withVersion(version =>
			this.http.put<Employee>(`${environment.apiUri}/${version}/employees`, employee)
		);
	}

	searchEmployees(searchTerm: string): any {
		let filteredEmployees: Employee[] = [];
		if (searchTerm) {
			const filterSearch = this.employeeListState?.filter(
				(employee: Employee) =>
					employee.data.firstName.toLowerCase().includes(searchTerm) ||
					employee.data.lastName.toLowerCase().includes(searchTerm) ||
					employee.externalId?.toLowerCase().includes(searchTerm)
			);

			if (filterSearch && filterSearch.length > 0) {
				filteredEmployees = filterSearch;
			}

			return filteredEmployees;
		}
	}
}
